import { deliveryControls } from '@bb/contracts';
import { Checkbox } from '@bb/ui';
import { ApiError, type CanvasSettings } from '../../../api/client.ts';
import { EXEMPTIONS } from '../../campaigns/schedule/deliveryControls.ts';
import { QuietHoursFields, SendingRateFields } from '../../campaigns/schedule/DeliveryControlsForm.tsx';
import type { WizardStepProps } from './EntryStep.tsx';
import { sendIssues } from './settings.ts';

type Send = CanvasSettings['send'];

/** A field set or removed, so «not set» stays absent in the stored settings. */
function withField<K extends keyof Send>(send: Send, key: K, value: Send[K] | undefined): Send {
  const next = { ...send };
  if (value === undefined) delete next[key];
  else next[key] = value;
  return next;
}

export interface SendSettingsStepProps extends WizardStepProps {
  /** The employee holds `messages.exemptions.manage`; without it the exemptions are shown but cannot change. */
  canManageExemptions: boolean;
  /** The last failed save; `exemption_not_allowed` (403) is explained at the exemptions. */
  saveError: unknown;
}

/**
 * Send settings of a Canvas (BB-113): quiet hours and a sending rate for every Message step, and what its messages may
 * skip. Shares the quiet hours and rate fields of campaign delivery controls (BB-102) and explains in which order the
 * rules apply.
 */
export function SendSettingsStep({ environmentId, settings, onChange, readOnly, canManageExemptions, saveError }: SendSettingsStepProps) {
  const send = settings.send;
  const issues = sendIssues(send);
  const set = (next: Send) => onChange({ ...settings, send: next });
  const effective = deliveryControls.effectiveExemptions('marketing', send.exemptions);
  const exemptionsLocked = readOnly || !canManageExemptions;
  const denied = saveError instanceof ApiError && saveError.code === 'exemption_not_allowed';
  return (
    <section aria-label="Send settings" className="canvas-wizard__panel">
      <h2>Send settings</h2>
      <section aria-label="How the rules apply" className="notice notice--neutral">
        <p>How the rules apply to each message of the Canvas:</p>
        <ol>
          <li>The user&apos;s consent to the channel always applies. Global frequency caps, suppression lists and the global control group apply too, unless an exemption below lets the messages skip them.</li>
          <li>The quiet hours and the sending rate here are the defaults of every Message step.</li>
          <li>A Message step may override the quiet hours and add exemptions in its own settings.</li>
          <li>The sending rate is shared: one limit for all steps of the Canvas together, next to the environment&apos;s channel limits (the lower one applies).</li>
        </ol>
      </section>

      <QuietHoursFields quiet={send.quietHours} issues={issues} readOnly={readOnly} onChange={(quietHours) => set(withField(send, 'quietHours', quietHours))} />
      <p className="hint">A message held back by quiet hours waits in its step and is checked again (caps, consent) when it goes out.</p>

      <SendingRateFields
        environmentId={environmentId}
        ratePerMinute={send.ratePerMinute}
        rateMaxDelayHours={send.rateMaxDelayHours}
        issues={issues}
        readOnly={readOnly}
        rateLabel="Canvas sends per minute (all steps together)"
        ratePlaceholder="No Canvas limit"
        onChange={(patch) => {
          const [key] = Object.keys(patch) as Array<'ratePerMinute' | 'rateMaxDelayHours'>;
          set(withField(send, key!, patch[key!]));
        }}
      />

      <fieldset className="audience-group" aria-label="Exemptions">
        <legend>Exemptions</legend>
        <p className="hint">What the messages of this Canvas may skip. A Message step&apos;s own exemptions add to these.</p>
        {!canManageExemptions && !readOnly && <p className="hint">Changing exemptions needs the messages.exemptions.manage permission.</p>}
        {EXEMPTIONS.map(({ flag, label, hint }) => (
          <div key={flag}>
            <Checkbox label={label} checked={effective[flag]} disabled={exemptionsLocked} onCheckedChange={(on) => set({ ...send, exemptions: { ...send.exemptions, [flag]: on } })} />
            <p className="hint">{hint}</p>
          </div>
        ))}
        {denied && (
          <p className="bb-field__error" role="alert">
            The settings were not saved: changing exemptions needs the messages.exemptions.manage permission. Put them back as they were to save the other changes.
          </p>
        )}
      </fieldset>
    </section>
  );
}
